import React, { lazy, Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import { initTheme } from '@an/ui/theme';
import { SessionProvider, useMeQuery } from './lib/session';
import { ToastProvider } from './lib/toast';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui';
import { Accept, Forgot, Impersonate, Login, Reset, Signup, SignupOrg } from './pages/Auth';

initTheme('light');

const qc = new QueryClient({ defaultOptions: { queries: { staleTime: 15_000, retry: (n, e) => (e as { status?: number }).status !== 401 && (e as { status?: number }).status !== 403 && (e as { status?: number }).status !== 404 && n < 2, refetchOnWindowFocus: false } } });

const P = (load: () => Promise<any>, name: string) => lazy(async () => ({ default: (await load())[name] as React.ComponentType<any> }));
const Home = P(() => import('./pages/Dashboards'), 'HomePage');
const DashboardsList = P(() => import('./pages/Dashboards'), 'DashboardsList');
const DashboardView = P(() => import('./pages/Dashboards'), 'DashboardView');
const Assets = P(() => import('./pages/Assets'), 'AssetsPage');
const Types = P(() => import('./pages/Masters'), 'TypesPage');
const Categories = P(() => import('./pages/Masters'), 'CategoriesPage');
const Events = P(() => import('./pages/Masters'), 'EventsPage');
const MapPage = P(() => import('./pages/Masters'), 'MapPage');
const Tickets = P(() => import('./pages/Service'), 'TicketsPage');
const WorkOrders = P(() => import('./pages/Service'), 'WorkOrdersPage');
const Procedures = P(() => import('./pages/Service'), 'ProceduresPage');
const Sites = P(() => import('./pages/Sites'), 'SitesPage');
const Parts = P(() => import('./pages/Sites'), 'PartsPage');
const Stocktake = P(() => import('./pages/Sites'), 'StocktakePage');
const Rules = P(() => import('./pages/Rules'), 'RulesPage');
const Geofences = P(() => import('./pages/Rules'), 'GeofencesPage');
const Devices = P(() => import('./pages/Devices'), 'DevicesPage');
const Commands = P(() => import('./pages/Devices'), 'CommandsPage');
const Catalog = P(() => import('./pages/Devices'), 'CatalogPage');
const Shop = P(() => import('./pages/Devices'), 'ShopPage');
const Campaigns = P(() => import('./pages/Devices'), 'CampaignsPage');
const Installer = P(() => import('./pages/Devices'), 'InstallerPage');
const Groups = P(() => import('./pages/Org'), 'GroupsPage');
const Users = P(() => import('./pages/Org'), 'UsersPage');
const Teams = P(() => import('./pages/Org'), 'TeamsPage');
const Packages = P(() => import('./pages/Org'), 'ServicePackagesPage');
const CustomFields = P(() => import('./pages/Org'), 'CustomFieldsPage');
const Settings = P(() => import('./pages/Settings'), 'SettingsPage');
const Transfers = P(() => import('./pages/Settings'), 'TransfersPage');
const Scan = P(() => import('./pages/Misc'), 'ScanPage');
const Claim = P(() => import('./pages/Misc'), 'ClaimPage');
const Consent = P(() => import('./pages/Misc'), 'OAuthConsent');
const Help = P(() => import('./pages/Misc'), 'HelpPage');

const Loading = () => <div className="flex h-64 items-center justify-center text-slate-400"><Spinner /></div>;

function StartRedirect({ to }: { to: string }) {
  const nav = useNavigate();
  useEffect(() => { if (to && to !== '/') nav(to, { replace: true }); }, []);
  return null;
}

function Authed() {
  const me = useMeQuery();
  const loc = useLocation();
  if (me.isLoading) return <Loading />;
  if (me.error || !me.data) {
    if ((me.error as { status?: number })?.status === 401 || (me.error as { status?: number })?.status === 403) {
      // signed in but no organization yet → org wizard
      return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
    }
    return <div className="p-8 text-sm text-red-700">Could not load your account. <button className="underline" onClick={() => me.refetch()}>Retry</button></div>;
  }
  const installerOnly = me.data.membership.capabilities.includes('installer') && me.data.membership.role === 'standard';
  return (
    <SessionProvider me={me.data}>
      <Layout>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={installerOnly ? <Navigate to="/installer" replace /> : <><StartRedirect to={me.data.membership.startPage} /><Home /></>} />
            <Route path="/assets" element={<Assets />} />
            <Route path="/assets/types" element={<Types />} />
            <Route path="/assets/categories" element={<Categories />} />
            <Route path="/events" element={<Events />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/parts" element={<Parts />} />
            <Route path="/parts/stocktake" element={<Stocktake />} />
            <Route path="/tickets" element={<Tickets />} />
            <Route path="/tickets/new" element={<Navigate to={`/tickets?new=1${loc.search.replace('?', '&')}`} replace />} />
            <Route path="/work-orders" element={<WorkOrders />} />
            <Route path="/procedures" element={<Procedures />} />
            <Route path="/sites" element={<Sites />} />
            <Route path="/dashboards" element={<DashboardsList />} />
            <Route path="/dashboards/:id" element={<DashboardView />} />
            <Route path="/rules" element={<Rules />} />
            <Route path="/rules/geofences" element={<Geofences />} />
            <Route path="/devices" element={<Devices />} />
            <Route path="/devices/commands" element={<Commands />} />
            <Route path="/devices/catalog" element={<Catalog />} />
            <Route path="/devices/shop" element={<Shop />} />
            <Route path="/devices/orders" element={<Shop />} />
            <Route path="/devices/campaigns" element={<Campaigns />} />
            <Route path="/devices/firmware" element={<Campaigns />} />
            <Route path="/installer" element={<Installer />} />
            <Route path="/org" element={<Navigate to="/org/groups" replace />} />
            <Route path="/org/groups" element={<Groups />} />
            <Route path="/org/users" element={<Users />} />
            <Route path="/org/teams" element={<Teams />} />
            <Route path="/org/service-packages" element={<Packages />} />
            <Route path="/org/custom-fields" element={<CustomFields />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/:section" element={<Settings />} />
            <Route path="/transfers" element={<Transfers />} />
            <Route path="/scan" element={<Scan />} />
            <Route path="/scan/claim/:code" element={<Claim />} />
            <Route path="/oauth/authorize" element={<Consent />} />
            <Route path="/help" element={<Help />} />
            <Route path="*" element={<div className="p-8 text-slate-500">Page not found.</div>} />
          </Routes>
        </Suspense>
      </Layout>
    </SessionProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={qc}>
      <ToastProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/reset" element={<Reset />} />
            <Route path="/accept" element={<Accept />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup/org" element={<SignupOrg />} />
            <Route path="/impersonate" element={<Impersonate />} />
            <Route path="/*" element={<Authed />} />
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(<React.StrictMode><App /></React.StrictMode>);

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => {}));
}
